<template>
  <IssueFieldsModalComponent
    :event="event"
    :exclude-from-search="excludeFromSearch"
    :flag-types="flagTypes"
    :disable="disable"
    @save="$emit('save', $event)"
  >
    <template #trailing>
      <template v-if="event.customAttributes">
        <EventActionFieldRowComponent
          v-for="(field, index) in event.customAttributes"
          :key="`field${index}`"
          :label="te(`module.sbb.${field.name}`) ? t(`module.sbb.${field.name}`) : field.name"
        >
          <InputRow
            v-slot="{ field: validationField }"
            :name="`event-custom-attribute-${field.name}`"
            :model-value="field.value"
            :required="field.required"
          >
            <LISAInputNumber
              v-if="field.type === IssueCustomAttributesType.NUMBER"
              v-bind="validationField"
              v-model="field.value"
              :placeholder="
                field.placeholder == null
                  ? undefined
                  : te(`module.sbb.${field.placeholder}`)
                    ? t(`module.sbb.${field.placeholder}`)
                    : field.placeholder
              "
              class="w-100"
            ></LISAInputNumber>
            <InputText
              v-if="field.type === IssueCustomAttributesType.TEXT"
              v-bind="validationField"
              v-model="field.value"
              class="w-100"
              :placeholder="
                field.placeholder == null
                  ? undefined
                  : te(`module.sbb.${field.placeholder}`)
                    ? t(`module.sbb.${field.placeholder}`)
                    : field.placeholder
              "
              auto-resize
              rows="1"
            />
            <Textarea
              v-if="field.type === IssueCustomAttributesType.TEXTAREA"
              v-bind="validationField"
              v-model="field.value"
              :placeholder="
                field.placeholder == null
                  ? undefined
                  : te(`module.sbb.${field.placeholder}`)
                    ? t(`module.sbb.${field.placeholder}`)
                    : field.placeholder
              "
              class="w-100"
              auto-resize
              rows="3"
            />
            <ToggleSwitch
              v-if="field.type === IssueCustomAttributesType.BOOLEAN"
              v-bind="validationField"
              v-model="field.value"
            ></ToggleSwitch>
          </InputRow>
        </EventActionFieldRowComponent>
      </template>
      <EventActionFieldRowComponent>
        <FMFileInlineUploadPreview
          v-model:files-ids="event.fileIds"
          :disabled="disableEvent"
          automatic-screenshot
        />
      </EventActionFieldRowComponent>
    </template>
  </IssueFieldsModalComponent>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import InputRow from "@/components/shared/form/InputRow.vue";
import { computed, inject, type PropType } from "vue";
import Textarea from "primevue/textarea";
import { FlagTypes, IssueCustomAttributesType } from "@/graphql";
import IssueFieldsModalComponent from "../../event/modal/IssueFieldsModalComponent.vue";
import { DefaultDateInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import { AbstractIssueModalComponentFields } from "../../event/EventHelper";
import injectAsserted from "@/functions/assert/injectAsserted";
import { DisableEventInjectionKey } from "../IssueTypes";
import EventActionFieldRowComponent from "../../EventActionFieldRowComponent.vue";
import type { IssueEditType } from "../../event/modal/EventEditType";
import LISAInputNumber from "../../../primevueWrapper/LISAInputNumber.vue";
import ToggleSwitch from "primevue/toggleswitch";
import InputText from "primevue/inputtext";
import FMFileInlineUploadPreview from "@/modules/filemanagement/components/FMFileInlineUploadPreview.vue";

defineOptions({
  inheritAttrs: true,
});

defineProps({
  event: {
    type: Object as PropType<IssueEditType>,
    required: true,
  },
  excludeFromSearch: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  flagTypes: {
    type: Array as PropType<FlagTypes[]>,
    required: true,
  },
});
defineEmits(["save"]);
const disableEvent = injectAsserted(DisableEventInjectionKey);
const defaultDate = inject(
  DefaultDateInjectionKey,
  computed(() => ({ isoString: "", editable: true })),
);

const disable = computed<Record<AbstractIssueModalComponentFields, boolean>>(() => {
  return {
    description: false,
    creator: false,
    date: !defaultDate.value.editable,
    flags: false,
    owner: false,
    eisenhowerPriority: false,
  };
});

const { t, te } = useTranslation();
</script>

<style lang="scss" scoped></style>
